import { Bell, Sidebar } from 'lucide-react';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@xanectech/ui/components';

import { useLocation } from 'react-router-dom';
import type { UserInfo, NotificationItem } from './dashboard-config';
import { ThemeToggle } from '../../shared/ThemeToggle';
import { Breadcrumb } from '../../shared/Breadcrumb';
import type { ReactNode } from 'react';
import { HelpTrigger } from '../../help';
import { useHelpSafe } from '../../contexts/HelpContext';

interface DashboardHeaderProps {
  routeTitles: Record<string, string>;
  user?: UserInfo;
  notifications?: NotificationItem[];
  onLogout?: () => void;
  showThemeToggle?: boolean;
  showNotifications?: boolean;
  showUserMenu?: boolean;
  mobileMenuTrigger?: ReactNode;
  onToggleSidebar?: () => void;
}

function DashboardHeader({
  routeTitles,
  user,
  notifications = [],
  onLogout,
  showThemeToggle = true,
  showNotifications = true,
  showUserMenu = true,
  mobileMenuTrigger,
  onToggleSidebar,
}: DashboardHeaderProps) {
  const location = useLocation();
  const helpdeskContext = useHelpSafe();

  const locationState =
    location.state && typeof location.state === 'object'
      ? (location.state as { name?: string })
      : null;

  const isUuidSegment = (segment: string) =>
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
      segment,
    ) || /^[0-9a-f]{32}$/i.test(segment);

  const formatSegmentTitle = (segment: string) =>
    segment
      .split('-')
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ') || segment;

  const getRouteTitle = (path: string, segment: string) => {
    if (isUuidSegment(segment) && locationState?.name) {
      return locationState.name;
    }

    if (routeTitles[path]) {
      return routeTitles[path];
    }

    const dynamicMatch = Object.entries(routeTitles).find(([route]) => {
      const routeParts = route.split('/').filter(Boolean);
      const pathParts = path.split('/').filter(Boolean);

      if (routeParts.length !== pathParts.length) return false;

      return routeParts.every(
        (routePart, index) =>
          routePart.startsWith(':') || routePart === pathParts[index],
      );
    });

    if (dynamicMatch) {
      return dynamicMatch[1];
    }

    return formatSegmentTitle(segment);
  };

  const currentPath = location.pathname;

  const pathSegments = currentPath.split('/').filter(Boolean);
  const pageTitle =
    pathSegments.length > 0
      ? getRouteTitle(currentPath, pathSegments[pathSegments.length - 1] ?? '')
      : routeTitles[currentPath] || 'Dashboard';

  const unreadNotifications = notifications.filter((n) => !n.read);

  return (
    <header className='border-b border-border bg-card'>
      <div className='flex h-16 items-center justify-between px-4 md:px-6'>
        <div className='flex items-center gap-3'>
          {/* {mobileMenuTrigger} */}
          <div className='flex flex-row gap-2 items-center'>
            <Button
              variant='glass'
              className='px-0 py-0 text-md font-semibold items-center justify-center mr-2 hover:text-primary'
              onClick={onToggleSidebar}
              aria-label='Toggle Sidebar'
            >
              <Sidebar className='h-3 w-3' />
            </Button>
            <h1 className='text-lg font-semibold text-foreground'>
              {pageTitle}
            </h1>
          </div>
        </div>

        <div className='flex items-center gap-2'>
          {helpdeskContext && (
            <HelpTrigger onClick={() => helpdeskContext.openPageHelp()} />
          )}
          {showThemeToggle && <ThemeToggle className='ml-auto' />}

          {showNotifications && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant='ghost'
                  size='icon'
                  className='relative h-10 w-10'
                >
                  <Bell className='h-5 w-5' />
                  {unreadNotifications.length > 0 && (
                    <span className='absolute right-1 top-1 flex h-4 w-4 items-center justify-center rounded-full bg-secondary text-[10px] font-bold text-destructive-foreground'>
                      {unreadNotifications.length}
                    </span>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align='end' className='w-80'>
                <DropdownMenuLabel>Notifications</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {notifications.length === 0 ? (
                  <DropdownMenuItem disabled>No notifications</DropdownMenuItem>
                ) : (
                  notifications.slice(0, 5).map((notification) => (
                    <DropdownMenuItem
                      key={notification.id}
                      className='flex flex-col items-start gap-1 py-3'
                    >
                      <span className='font-medium'>{notification.title}</span>
                      <span className='text-xs text-muted-foreground'>
                        {notification.timestamp}
                      </span>
                    </DropdownMenuItem>
                  ))
                )}
                {notifications.length > 5 && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem className='justify-center text-primary'>
                      View all notifications
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          {showUserMenu && user && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant='ghost' className='h-10 gap-2 px-2'>
                  {user.avatarUrl ? (
                    <img
                      src={user.avatarUrl}
                      alt={user.name}
                      className='h-8 w-8 rounded-full object-cover'
                    />
                  ) : (
                    <div className='flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground'>
                      <span className='text-sm font-semibold'>
                        {user.initials}
                      </span>
                    </div>
                  )}
                  <span className='hidden text-sm font-medium md:inline'>
                    {user.name}
                  </span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align='end' className='w-56'>
                <DropdownMenuLabel>My Account</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem>Profile</DropdownMenuItem>
                <DropdownMenuItem>Settings</DropdownMenuItem>
                <DropdownMenuItem>Billing</DropdownMenuItem>
                <DropdownMenuSeparator />
                {onLogout && (
                  <DropdownMenuItem
                    className='text-destructive'
                    onClick={onLogout}
                  >
                    Log out
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
      <Breadcrumb
        routeTitles={routeTitles}
        className='hidden sm:flex px-4 md:px-6 h-8 border-t border-border/50'
      />
    </header>
  );
}

export { DashboardHeader, type DashboardHeaderProps };
